import { HlsConfig } from 'hls.js'

import { MediaContentSource } from './linesTypes'
import { PageDataType, PageType, SectionGridGroupType } from './pageTypes'
import { SearchQuery } from './searchTypes'
import { LineTileShape, LineTileSize, lineTypes } from '../../constants/common'
import { YoutubeTileType } from '../sliderTypes'
import { EventDateType } from '../utilsTypes'
import { CreditWalletType } from './walletTypes'

export type ConfigType = {
  specialHomePageId?: string
  homePageId?: string
  focusPageId?: string
  selectedPageId?: string
  sportsPageId?: string
  cataloguePageIds?: Array<string>
  activeRegionId?: string
  regionId?: string
  availableRegions?: Array<string>
  version: string
  home?: PageType
  selected?: PageType
  specialHome?: PageType
  sportsPage?: PageType
  focusPage?: PageType
  calendar?: Array<CalendarType>
  sports?: Array<SportType>
  catalogue?: PageType[]
  welcomeScreenCounter: number
  showWelcomeScreenForModelYear: string[]
  limitTilesPerLine: number
  channelsLimit: number
  cataloguePagesLimit: number
  limitSpacesPerGrid: number
  channelEventsLimit: number
  refreshInterval: number
  cacheDuration: number
  disabledMenuItems: Nullable<Array<string>>
  hlsOptions?: Partial<HlsConfig>
  allowBlurForPremiumChannels?: boolean
  useUtcOffsetMins?: boolean
  palette?: PaletteType
  useCache?: boolean
  featureScreens?: Array<FeatureScreenType>
  region?: RegionIdType
}

export type FeatureScreenType = {
  id: string
  counter: number
  featureButtonLink?: Nullable<string>
  featureButtonTitle?: string
  image: ImageType
}

export type RegionInfoType = {
  currentRegionId: string
  mainRegionId?: string
  availableRegionIds?: Array<string>
}

export type RegionIdType = {
  id: string
  name?: string
  countryCodes: Array<string>
  privacyRegulation: 'None' | 'Gdpr' | 'Ccpa' | 'Tcf'
  userCentricsId?: string
}

export type SportType = {
  name: string
  categoryId: string
  lines: Array<LineType>
}

export type CalendarType = {
  name: string
  date: number
  lines: Array<LineType>
  palette?: PaletteType
}

export type LineDataType = {
  data: Array<TileType | YoutubeTileType>
  name?: string
  next?: string
  sliderId: string
  type?: lineTypes
  allowHideIfEmpty?: boolean
  isSearchListType?: boolean
  searchResultType?: SearchQuery
  tileShape?: LineTileShape
  tileSize?: LineTileSize
}

export type LineType = {
  id?: string
  lineContextId: string
  name?: string
  liveToday?: boolean
  allowHideIfEmpty?: boolean
  lineType?: string
  categoryId?: string
  tileShape?: LineTileShape
  tileSize?: LineTileSize
}

export type TileType = {
  id: string
  title?: string
  previewImage?: ImageType
  image?: ImageType
  preview?: PreviewType
  shade?: boolean
  type?: tileTypes
  size: SizeType
  position: PositionType
  data?: TileDataType
  showTitle?: boolean
  accessRightIds?: Array<string>
}

export type TileDataType = {
  mediaPlayer?: MediaPlayerType
  channelListPlayer?: { channels: Array<ChannelType> }
  channelPlayer?: { channel?: ChannelType }
  channel?: ChannelType
  broadcast?: BroadcastType
  pageId?: string
  event?: EventType
  media?: MediaType
  ad?: AdType
  htmlWidget?: HTMLWidgetType
  deepLink?: DeepLinkType
  mediaData?: { video?: MediaType; image?: ImageType; partnerIds?: PartnerLinkType[] }
}

type MediaPlayerType = {
  title?: string
  video?: MediaType
}

export type PayPerViewInfoType = {
  id: string
  walletId: string
  cost: number
  accessRightId: string
}

export type UserAccessRightsReasonType = {
  type: 'Subscription' | 'Ppv' | 'Free'
  id?: string
  validFrom?: number
  validUntil?: number
}

export type UserRightsType = {
  canAccess: boolean
  reasons?: Array<UserAccessRightsReasonType>
}

export type PartnerLinkType = {
  id?: string
  type?: string
}

export type EventDataType = {
  id: string
  categoryId?: string
  name?: string
  description?: DescriptionType
  image?: ImageType
  channelId?: string
  startTime: number
  endTime: number
  nativewaves?: Nativewaves
  preview?: PreviewType
  live?: boolean
  type?: eventTypes
  shareable?: boolean
  video?: MediaType
  deepLink?: DeepLinkType
  accessRightIds?: Array<string>
  adSettings?: ResolvedAdSettings
  userRights?: UserRightsType
  ppvInfo?: Array<PayPerViewInfoType>
  partnerIds?: PartnerLinkType[]
  watchable?: boolean
  public?: boolean
  channelLogo?: ImageType
  title?: string // <-- not in Swagger, but present in the API
  requiresRegistration?: boolean
}

export type EventType = EventDataType & {
  categoryName?: string // <-- not in the API
  palette?: PaletteType // <-- not in the API
  backgroundImage?: ImageType // <-- not in the API
  listOptions?: ListPopupType // <-- not in the API
  isFutureChannelEvent?: boolean // <-- not in the API
}

export type PositionType = {
  horizontal: boolean
  x: number
  y: number
  index: number
}

type AdType = {
  type: string
  contentURL: string
  mobileURL: string
  sizeHint: number
  deepLink: string
  pos: PositionType
}

export type SizeType = {
  width: number
  height: number
  minWidth: number
  minHeight: number
}

type MediaType = {
  id?: string
  sources?: MediaSourceType[]
  onEnded?: EndActions
}

export type MediaSourceType = {
  format: 'Hls' | 'Mp4'
  url?: string
}

export type VideoSourceType = MediaSourceType | ImageSourceType | MediaContentSource

export type DeepLinkType = {
  ios?: {
    url?: string
  }
  android?: {
    url?: string
  }
  tizen?: {
    appId?: string
    appName?: string
    payload?: string
  }
}

export type HTMLWidgetType = {
  url: string
  platformUrls?: Array<PlatformURL>
  renderInTile?: boolean
}

type PlatformURL = {
  url: string
  defaultFilterAll: boolean
  platformFilter?: Array<string>
}

export type DescriptionType = {
  short?: string
  long?: string
  extended?: string
}

export type Nativewaves = {
  enabled?: boolean
  /**
   * NativeWaves-specific event ID (not the Sportworld one). Not used on FE
   * @deprecated
   */
  eventId?: string
  appConfigId?: string
  /**
   * Optional manifest ID override
   */
  manifestId?: string
  enableSync?: boolean
  smdsDirectSyncId?: string
  legacySmdsEndpointUrl?: string
}

export type BroadcastType = {
  brodcastId: string
  activeSlide: StageSlide
  slides: StageSlide[]
}

export type ChannelType = {
  id: string
  isVideoAvailable?: boolean
  previewImage?: ImageType
  image?: ImageType
  name: string
  nameLong?: string
  description?: string
  deepLink?: DeepLinkType
  htmlWidgetUrl?: string
  video?: VideoType
  freeVideo?: VideoType
  userRights?: UserRightsType
  accessRightIds?: Array<string>
  public?: boolean
  watchable?: boolean
  knownNames?: Array<string>
  locators?: Array<string>
  premiumLockedscreenImage?: ImageType
  palette?: PaletteType
  backgroundImage?: ImageType
  adSettings?: ResolvedAdSettings
  requiresRegistration?: boolean
  partnerIds?: PartnerLinkType[]
}

export type ResolvedAdSettings = {
  ssaiUrlParameters?: string
  csaiAdUrl?: string
  csaiSettings?: {
    preRollAdUrl?: string
    postRollAdUrl?: string
    midRolls?: {
      adUrl?: string
      contentPosition: number
    }[]
  }
}

export type ImageType = {
  id?: string
  sources?: Array<ImageSourceType>
}

export type PreviewType = {
  image?: ImageType
  autoPlay?: boolean
}
export type ImageSourceType = {
  format: string
  width: number
  height: number
  url?: string
  darkTheme: boolean
  oversize?: string
  aspectDiff?: number
}

export type MediaContentType = {
  id?: string
  channelId?: string
  channel?: ChannelType
  categoryId?: string
  videoDataType?: VideoDataType
  sources?: Array<VideoSourceType>
  name?: string
  contentType?: mediaDataTypes | tileTypes | eventTypes
  isImageType?: boolean
  eventDate?: EventDateType
  listOptions?: ListPopupType
  isHtmlWidgetType?: boolean
  htmlWidgetUrl?: string
  onEnded?: EndActions
  adSettings?: Nullable<ResolvedAdSettings>
  eventEndTime?: number
  redirectSearch?: string
  partnerIds?: PartnerLinkType[]
}

export type ListPopupType = {
  listContextId?: string
  lineContextId?: string
  isSearchListType?: boolean
  name?: string
  tileShape?: LineTileShape
  searchResultType?: SearchQuery
  activeTileId?: string
  activeTileIndex?: number
}

export type VideoType = MediaContentType
export type VideoDataType =
  | eventTypes
  | tileTypes.EVENT
  | tileTypes.MEDIA
  | tileTypes.MEDIA_PLAYER
  | tileTypes.AD
  | tileTypes.CHANNEL

export type AccessRightItemData = {
  lifeTimeStart?: string
  lifeTimeEnd?: string
  pauseTimeStart?: string
  pauseTimeEnd?: string
  maxCacheDate?: string
  data?: { [key: string]: string }
  blocked: boolean
  productId?: string
  plenigoOfferId?: string
  plenigoProductId?: string
  plenigoStepId?: string
  accessRightUniqueId?: subscriptions
  itemType?: 'EXTERNAL' | 'SINGLE_PRODUCT' | 'SUBSCRIPTION_ITEM'
  itemId?: string
  accessRightData?: { [key: string]: string }
}

export type OfferInfoType = {
  provider: any
  id: string
  accessRightUniqueId: string
}

export type AccessRightsType = {
  customerId: string
  customerBlocked?: boolean
  items?: AccessRightItemData[]
}

export type OfferType = {
  id: string
  title: string
  description: string
  callToActionText: string
  image?: ImageType
  offerInfo?: OfferInfoType
  showToUser?: boolean
  userRights?: UserRightsType
  creditPackageInfo?: {
    credits?: number
    cost?: number
  }
}

type ProductTagType = {
  productTagId: number
  name: string
  description: string
  category: string
}

type Translation = {
  language: string
  title: string
  description: string
  legalText: string
  summaryText: string
  preferred: boolean
}

type StepType = {
  plenigoStepId: string
  durationTimespan: string
  accountingTimespan: string
  productTags: ProductTagType[]
  priceIssueId: number
  translations: Translation[]
  position: number
  termPeriod: number
  termTimespan: string
  durationPeriod: number
  accountingPeriod: number
  cancellationPeriod: number
  cancellationTimespan: string
  ivwRuleId: number
  creditCount: number
}

type ProductType = {
  plenigoProductId: string
  steps: StepType[]
  productTags: ProductTagType[]
  internalTitle: string
  position: number
  taxType: string
  productType: string
  subscription: boolean
  amount: number
  validityTime: number
  accessRightId: number
  costCenterId: number
  ledgerId: number
  ivwRuleId: number
  voucherTargetPlenigoOfferId: string
  voucherTemplateId: number
  voucherValidityTime: number
  voucherValidityTimespan: string
  priceIssueId: number
  creditCountWalledId: number
  creditCount: number
  option: boolean
  accessRightUniqueId: string
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type OfferItemType = {
  plenigoOfferId: string
  products: ProductType[]
  productTags: ProductTagType[]
  internalTitle: string
  pauseAble: boolean
  invoiceAddressMandatory: boolean
  deliveryAddressMandatory: boolean
  multiplePurchases: boolean
  misuseRuleId: number
  archived: boolean
  allowedPaymentMethods: any[]
  leafId: number
  externalBilling: boolean
  customerCancellationBlocked: boolean
  corporateAccountUsers: number
  bonusId: number
  managedExternal: boolean
  translations: Translation[]
}

export type PaletteType = {
  id?: string
  name?: string
  colors?: {
    text?: string
    primary?: string
    secondary?: string
    accent?: string
    altAccent?: string
    // ppvText?: string
    // ppvBg?: string
    // promotionBg?: string
    // promotionText?: string
    badgeLiveBg?: string
    badgeTimeBg?: string
    textDark?: string
    translucent?: string
    shade?: string
    gradient?: {
      start?: string
      end?: string
    }
    videoBg?: string
    body?: string
    title?: string
    link?: string
    app?: {
      background?: { start?: string; end?: string }
    }
    popup?: {
      background?: { start?: string; end?: string }
    }
    sidebar?: {
      background?: string
      expandSidebarBackground?: { start?: string; end?: string }
      collapseSidebarBackground?: { start?: string; end?: string }
      settingsSidebarBackground?: { start?: string; end?: string }
      blurBackground?: { start?: string; end?: string }
      border?: string
      text?: string
      focusedElem?: {
        background?: string
        border?: string
      }
      activeElem?: {
        background?: string
        border?: string
      }
    }
    welcomeScreen?: {
      text?: string
      title?: string
      focusedElem?: {
        background?: string
        border?: string
      }
    }
    account?: {
      text?: string
      title?: string
      separator?: string
    }
    register?: {
      text?: string
      title?: string
      separator?: string
      backBtnBg?: string
    }
    logout?: {
      text?: string
      title?: string
    }
    homeGrid?: {
      background?: string
      focusedElem?: {
        border?: string
      }
    }
    eventDetail?: {
      text?: string
      border?: string
      coloredText?: string
      background?: string
    }
    developer?: {
      text?: string
      title?: string
      background?: string
      focusedElem?: {
        border?: string
      }
    }
    calendar?: {
      text?: string
      focusedElem?: {
        background?: string
        border?: string
      }
      activeElem?: {
        background?: string
        border?: string
      }
    }
    countdown?: {
      text?: string
      title?: string
      background?: string
      textBackground?: string
      focusedElem?: {
        border?: string
      }
    }
    dialogPopup?: {
      text?: string
      title?: string
      overlay?: string
      background?: { start?: string; end?: string }
      border?: string
    }
    dotIndicator?: {
      background?: string
      activeElem?: {
        background?: string
      }
    }
    player?: {
      background?: string
      controlsBg?: string
      focusedElem?: {
        border?: string
      }
    }
    pageHeader?: {
      text?: string
      title?: string
    }
    settingsHeader?: {
      text?: string
      title?: string
    }
    tabs?: {
      text?: string
      separator?: string
      focusedElem?: {
        background?: string
        border?: string
      }
      activeElem?: {
        background?: string
        border?: string
      }
    }
    dataWidget?: {
      text?: string
      accent?: string
      background?: string
      liveText?: string
      liveBg?: string
    }
    tiles?: {
      title?: string
      text?: string
      liveText?: string
      liveBg?: string
      shading?: string
      progressBg?: string
      progressLine?: string
      focusedElem?: {
        border?: string
      }
      activeElem?: {
        background?: string
      }
    }
    squareTile?: {
      text?: string
      background?: string
      focusedElem?: {
        background?: string
        border?: string
      }
    }
    emptyTile?: {
      background?: string
      focusedElem?: {
        border?: string
      }
    }
    mediaPlayerTile?: {
      background?: string
      focusedElem?: {
        border?: string
      }
    }
    qrCode?: {
      background?: string
      text?: string
      title?: string
      link?: string
    }
    button?: {
      primary?: string
      accent?: string
      background?: string
      border?: string
      text?: string
      focusedElem?: {
        border?: string
        background?: string
      }
    }
    toggle?: {
      background?: string
      border?: string
      toggleColor?: string
      focusedElem?: {
        border?: string
      }
      checkedElem?: {
        background?: string
        border?: string
        toggleColor?: string
      }
    }
    lineLoader?: {
      background?: string
    }
    sectionGrid?: {
      title?: string
      focusedElem?: {
        border?: string
      }
    }
    subscription?: {
      background?: string
      inActiveElem?: string
      focusedElem?: {
        border?: string
      }
    }
    search?: {
      text?: string
      background?: string
      border?: string
      focusedElem?: {
        border?: string
        background?: string
      }
    }
    broadcast?: {
      background?: string
      channelBackground?: string
      logoBackground?: string
      text?: string
      title?: string
      focusedElem?: {
        background?: string
        border?: string
      }
      activeElem?: {
        background?: string
      }
    }
  }
}

export type YouTubeContentType = {
  id?: string
  name?: string
  description?: DescriptionType
  partnerIds?: PartnerLinkType[]
}

enum mediaDataTypes {
  CHANNEL = 'Channel',
  EVENT = 'Event',
  MEDIA = 'Media',
}

export enum subscriptions {
  SUPER_PREMIUM = 'superpremium',
  SAMSUNG_SPORT_PAKET = 'samsungsportpaket',
}

export enum eventTypes {
  CHANNEL = 'Channel',
  DEEP_LINK = 'DeepLink',
  VOD = 'Vod',
  YOUTUBE = 'YouTube',
}

export enum tileTypes {
  CHANNEL_LIST_PLAYER = 'ChannelListPlayer',
  CHANNEL_PLAYER = 'ChannelPlayer',
  MEDIA_PLAYER = 'MediaPlayer',
  EVENT = 'Event',
  PAGE = 'Page',
  MEDIA = 'Media',
  CHANNEL = 'Channel',
  YOUTUBE = 'youtube',
  AD = 'Ad',
  HTML_WIDGET = 'HtmlWidget',
  DEEP_LINK = 'DeepLink',
  DATA_WIDGET = 'DataWidget',
  SHOW_MORE = 'ShowMore',
}

export enum EndActions {
  LOOP = 'loop',
  CLOSE = 'close',
}

export type StagePromotionDataType = {
  id: string
  partner: {
    id: string
    title: string
    page?: PageDataType
    walletId?: string
  }
  title: string
  description?: string
  image?: ImageType
  logo?: ImageType
  startDate: number
  endDate: number
  page?: PageDataType
  additionalPartnerPageId?: string // TODO: make sure type in swagger is the same
  wallet?: CreditWalletType
  //offers?:
}

export const enum StageSlideType {
  PAGE = 'Page',
  CHANNEL = 'Channel',
  EVENT = 'Event',
  MEDIA = 'Media',
  PROMOTION = 'Promotion',
}
export type StageSlide = {
  id: string
  type: StageSlideType
  page?: PageDataType
  pageLink?: string //optional
  channel?: ChannelType //if type.Channel
  event?: EventType //if type.Event
  promotion?: StagePromotionDataType
  timer?: number //optional;
  title?: string //optional;
  description?: string //optional;
  buttonText?: string //optional;
  shade?: boolean //optional;
  media?: { video?: MediaType; image?: ImageType; partnerIds?: PartnerLinkType[] }
  image?: ImageType //optional;
  video?: MediaType
  timerBehaviour: StageTimerBehaviorTypes
}

export type Stage = {
  slides: StageSlide[]
  timer?: number
  timerBehaviour: StageTimerBehaviorTypes
}

export type StageLinesPage = {
  stage: Stage
  lines: LineType[]
  sectionGrid?: SectionGridGroupType
}

export const enum StageTimerBehaviorTypes {
  NONE = 'None',
  BUTTON_ACTION = 'ButtonAction',
  NEXT_SLIDE = 'NextSlide',
}

export enum HomeTabTypes {
  STAGE_LINES = 'StageLinesPage',
  STAGE_SECTION_GRID = 'StageSectionGridPage',
  LINES = 'Lines',
  SECTION_GRID = 'SectionGrid',
  SEARCH = 'Search',
}

export type HomeTab = {
  id: string
  title: string
  type: HomeTabTypes
  stageLinesPage?: StageLinesPage
  lines?: LineType[]
  sectionGrid?: SectionGridGroupType
}

export type AppSettingsType = {
  isCustomPaletteUsed: boolean
  isDevToolsUsed: boolean
  isPerformanceMonitorUsed: boolean
  isDisabledBlur: boolean
  isDisabledImages: boolean
  isDisabledHtmlWidgets: boolean
  isDisabledLines: boolean
  isFullscreenAutoLandscapeUsed: boolean
  isEnabledAdSettingsOverride: boolean
  isEnabledImaSdk: boolean
  isEnabledImaSdkDebug: boolean
  isAlwaysGoToDetailPage: boolean
  isDisabledStageAutoslide: boolean
  isEnabledChannelEventMockData: boolean
}

export type PartnerType = {
  id: string
  title: string
  page?: PageType
  walletId?: string
}

export type PromotionType = {
  id: string
  title: string
  partner: PartnerType
  description?: string
  image?: ImageType
  logo?: ImageType
  startDate: number
  endDate: number
  page?: PageType
  pageId?: string
  additionalPartnerPageId?: string
  wallet?: CreditWalletType
  offers?: OfferType[]
  hasVouchers?: boolean
}
